import i18n from "../i18n";
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { CircleMarker, Circle, MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import Map, { Source, Layer } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import "../pages/heatmap-maplibre.css";
import { isMapLibreEnabled, maptilerStyleUrl } from "../config/mapProvider";

/**
 * Listing-detail mini map (web). Mirrors the mobile ListingMiniMap:
 * small, non-interactive preview with a location circle. Welle 5
 * Phase 4 — hint + circle radius follow the listing's `geo_precision`.
 * Karten-Migration — renders via MapLibre GL (Basic Dark) behind the
 * provider flag; Leaflet stays as the temporary fallback.
 */

// Approximate geodesic circle as a polygon (metres → degrees).
function circlePolygon(lat, lng, radiusM, points = 48) {
  const coords = [];
  const dLat = radiusM / 111320;
  const dLng = radiusM / (111320 * Math.cos((lat * Math.PI) / 180));
  for (let i = 0; i <= points; i += 1) {
    const a = (i / points) * 2 * Math.PI;
    coords.push([lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return {
    type: "Feature",
    geometry: { type: "Polygon", coordinates: [coords] },
    properties: {},
  };
}

function MiniMapLibre({ lat, lng, radiusM, zoom, showPoint }) {
  const circle = useMemo(
    () => circlePolygon(lat, lng, radiusM),
    [lat, lng, radiusM],
  );
  const point = useMemo(
    () => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: [lng, lat] },
      properties: {},
    }),
    [lat, lng],
  );
  return (
    <Map
      initialViewState={{ latitude: lat, longitude: lng, zoom }}
      mapStyle={maptilerStyleUrl()}
      style={{ height: "100%", width: "100%", background: "#18181b" }}
      attributionControl={{ compact: true }}
      dragPan={false}
      scrollZoom={false}
      doubleClickZoom={false}
      dragRotate={false}
      touchZoomRotate={false}
      touchPitch={false}
      keyboard={false}
      interactive={false}
    >
      {/* createElement keeps the dev-mode babel plugin from injecting
          x-* attrs into the maplibre source/layer specs. */}
      {React.createElement(
        Source,
        { key: "minimap-circle", id: "minimap-circle", type: "geojson", data: circle },
        React.createElement(Layer, {
          id: "minimap-circle-fill",
          type: "fill",
          paint: { "fill-color": "rgba(245,158,11,0.25)" },
        }),
        React.createElement(Layer, {
          id: "minimap-circle-line",
          type: "line",
          paint: { "line-color": "rgba(245,158,11,0.9)", "line-width": 2 },
        }),
      )}
      {showPoint &&
        React.createElement(
          Source,
          { key: "minimap-point", id: "minimap-point", type: "geojson", data: point },
          React.createElement(Layer, {
            id: "minimap-point-circle",
            type: "circle",
            paint: {
              "circle-color": "#f59e0b",
              "circle-radius": 6,
              "circle-stroke-color": "#18181b",
              "circle-stroke-width": 1,
            },
          }),
        )}
    </Map>
  );
}

export default function MiniMap({
  lat, lng, ulid, vertical, country, locationLabel, geoPrecision,
  approxLat, approxLng, approxSource,
}) {
  const hasCoords = typeof lat === "number" && typeof lng === "number";
  // V4.10 Phase 11 / V4.11 — Mini-Map fallback CASCADE: the backend
  // resolves city_centroid → city_average → country_centroid, so the
  // map is NEVER empty as long as a country exists.
  const hasApprox =
    !hasCoords && typeof approxLat === "number" && typeof approxLng === "number";
  const isCountryFallback = hasApprox && approxSource === "country_centroid";

  if (!hasCoords && !hasApprox) {
    return (
      <div
        data-testid="mini-map-fallback"
        className="rounded-2xl border border-zinc-800 bg-zinc-950/60 px-5 py-4 text-sm text-zinc-500"
      >
        {locationLabel
          ? i18n.t("common:minimap.roughLocation", { label: locationLabel })
          : i18n.t("common:minimap.unavailable")}
      </div>
    );
  }

  const precise = hasCoords && (geoPrecision === "exact" || geoPrecision === "street");
  const cLat = hasCoords ? lat : approxLat;
  const cLng = hasCoords ? lng : approxLng;
  const radiusM = hasCoords
    ? (precise ? 120 : 600)
    : isCountryFallback ? 60000 : 2500;
  const zoom = hasCoords ? (precise ? 15 : 13) : isCountryFallback ? 6 : 11;

  const heatmapHref = `/heatmap?v=${vertical === "auto" ? "auto" : "immo"}&focus_lat=${cLat}&focus_lng=${cLng}${
    hasCoords && ulid
      ? `&focus_ulid=${encodeURIComponent(ulid)}&origin_ulid=${encodeURIComponent(ulid)}`
      : ""
  }${country ? `&focus_country=${encodeURIComponent(country)}` : ""}`;

  return (
    <div data-testid="mini-map" className="space-y-2">
      <div className="relative rounded-2xl overflow-hidden border border-zinc-800 h-56">
        {isMapLibreEnabled() ? (
          <MiniMapLibre
            lat={cLat}
            lng={cLng}
            radiusM={radiusM}
            zoom={zoom}
            showPoint={hasCoords}
          />
        ) : (
          <MapContainer
            center={[cLat, cLng]}
            zoom={zoom}
            scrollWheelZoom={false}
            dragging={false}
            doubleClickZoom={false}
            zoomControl={false}
            attributionControl={false}
            className="h-full w-full"
            style={{ background: "#18181b" }}
          >
            <TileLayer url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png" />
            <Circle
              center={[cLat, cLng]}
              radius={radiusM}
              pathOptions={{
                color: "rgba(245,158,11,0.9)",
                fillColor: "rgba(245,158,11,0.25)",
                fillOpacity: 0.25,
                weight: 2,
              }}
            />
            {hasCoords && (
              <CircleMarker
                center={[cLat, cLng]}
                radius={6}
                pathOptions={{ color: "#f59e0b", fillColor: "#f59e0b", fillOpacity: 1 }}
              />
            )}
          </MapContainer>
        )}
        {!precise && (
          <div
            data-testid="mini-map-approx-badge"
            className="absolute top-2 left-2 z-[500] rounded-full border border-zinc-700/70 bg-zinc-950/80 px-2 py-0.5 text-[10px] text-zinc-300 pointer-events-none"
          >
            {i18n.t("common:minimap.approxBadge")}
          </div>
        )}
        {hasCoords && (
          <div
            data-testid="mini-map-search-radius-badge"
            className="absolute bottom-3 left-3 z-[500] rounded-full border border-zinc-700/70 bg-zinc-950/80 px-2 py-0.5 text-[10px] text-zinc-300 pointer-events-none"
          >
            {i18n.t("common:minimap.searchRadiusBadge")}
          </div>
        )}
        <Link
          to={heatmapHref}
          data-testid="mini-map-open-heatmap"
          className="absolute bottom-3 right-3 z-[500] inline-flex items-center gap-1.5 rounded-full border border-amber-500/40 bg-zinc-950/85 px-3 py-1.5 text-xs font-medium text-amber-300 hover:bg-zinc-900"
        >
          {i18n.t("common:minimap.openHeatmap")}
        </Link>
      </div>
      <div
        data-testid="mini-map-approx-hint"
        data-precision={
          hasApprox
            ? (isCountryFallback ? "country_fallback" : "city_fallback")
            : geoPrecision || "unknown"
        }
        className="text-[11px] text-zinc-500"
      >
        {hasApprox
          ? (isCountryFallback
              ? i18n.t("common:minimap.countryFallbackHint")
              : i18n.t("common:minimap.cityFallbackHint"))
          : precise
            ? i18n.t("common:minimap.streetHint")
            : i18n.t("common:minimap.approxHint")}
      </div>
    </div>
  );
}
